import { useMemo } from 'react';
import { QuestionFieldConfig } from '../utils/questionMapper';

export interface FieldDefinition {
  name: string;
  type: 'text' | 'textarea' | 'radio' | 'select' | 'multiselect' | 'date' | 'daterange' | 'datetime';
  label: string;
  required: boolean;
  options?: Array<{ id: string; label: string; disabled?: boolean }>;
  props: Record<string, any>;
  domainType?: string;
}

interface UseQuestionnaireFieldsProps {
  fieldConfigs: QuestionFieldConfig[];
  isReadOnly?: boolean;
}

/**
 * Hook to convert field configurations to renderable field definitions
 */
export const useQuestionnaireFields = ({ fieldConfigs, isReadOnly = false }: UseQuestionnaireFieldsProps): FieldDefinition[] =>
  useMemo(() => {
    const result = fieldConfigs.map((config) => {
      const baseProps = {
        name: config.name,
        label: config.label,
        required: config.required,
        isReadOnly,
      };

      switch (config.type) {
        case 'TEXT': {
          const textField = {
            name: config.name,
            type: 'textarea' as const,
            label: config.label,
            required: config.required,
            domainType: config.domainType,
            props: {
              ...baseProps,
              rows: 6,
              multiline: true,
            },
          };
          return textField;
        }

        case 'RADIO':
          return {
            name: config.name,
            type: 'radio' as const,
            label: config.label,
            required: config.required,
            options: config.options,
            domainType: config.domainType,
            props: {
              ...baseProps,
              options: config.options || [],
            },
          };

        case 'SELECT':
          // Handle special case for charge selection
          if (config.domainType === 'SELECT_A_CHARGE_NAME') {
            return {
              name: config.name,
              type: 'select' as const,
              label: config.label,
              required: config.required,
              domainType: config.domainType,
              props: {
                ...baseProps,
                answerId: config.options?.[0]?.id,
                // This will be handled by RhfFieldSelectSubChargeDomain
              },
            };
          }

          return {
            name: config.name,
            type: 'select' as const,
            label: config.label,
            required: config.required,
            options: config.options,
            domainType: config.domainType,
            props: {
              ...baseProps,
              options: config.options || [],
            },
          };

        case 'MULTIPLE_SELECT':
          return {
            name: config.name,
            type: 'multiselect' as const,
            label: config.label,
            required: config.required,
            options: config.options,
            domainType: config.domainType,
            props: {
              ...baseProps,
              options: config.options || [],
              multiple: true,
            },
          };

        case 'DATE':
          return {
            name: config.name,
            type: 'date' as const,
            label: config.label,
            required: config.required,
            domainType: config.domainType,
            props: baseProps,
          };

        case 'DATE_TIME':
          return {
            name: config.name,
            type: 'datetime' as const,
            label: config.label,
            required: config.required,
            domainType: config.domainType,
            props: baseProps,
          };

        case 'DATE_RANGE':
          return {
            name: config.name,
            type: 'daterange' as const,
            label: config.label,
            required: config.required,
            domainType: config.domainType,
            props: baseProps,
          };

        default:
          // Fallback to text field
          return {
            name: config.name,
            type: 'text' as const,
            label: config.label,
            required: config.required,
            domainType: config.domainType,
            props: baseProps,
          };
      }
    });

    return result;
  }, [fieldConfigs, isReadOnly]);
